import { motion } from 'framer-motion';
import { Lock, Database, Zap } from 'lucide-react';

const cards = [
  {
    icon: Lock,
    tag: 'Verifiable Outputs',
    title: 'Proof Engine',
    desc: 'Proof-of-Inference + optional zero-knowledge guarantees ensure every robot decision is traceable, auditable, and insurable.',
    bullets: ['Cryptographic receipts per action', 'Optional zk-SNARK layer', 'Regulator-ready lineage'],
  },
  {
    icon: Database,
    tag: 'Tokenized Assets',
    title: 'RWA Registry',
    desc: 'GPUs, energy compute windows, licensed datasets, and storage capacity — all tokenized as Real World Assets on-chain.',
    bullets: ['On-chain GPU ownership', 'Energy-window auctions', 'nDATA-R licensing tokens'],
    featured: true,
  },
  {
    icon: Zap,
    tag: 'Built on Solana',
    title: 'Solana Core',
    desc: 'Speed, cost efficiency, and sub-second finality purpose-built for real-time robotics at industrial scale.',
    bullets: ['400ms block time', '< $0.001 per transaction', 'Solana DePIN ecosystem'],
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] } },
};

export default function TechnologySection() {
  return (
    <section
      id="technology"
      data-testid="technology-section"
      className="relative py-28 md:py-36 overflow-hidden"
      style={{ background: '#000' }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
        {/* Header */}
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <p className="font-mono text-[#00F0FF] text-xs tracking-[0.35em] uppercase mb-4">
            The Stack
          </p>
          <div className="flex flex-col md:flex-row md:items-end gap-6 justify-between">
            <h2
              className="font-display text-white font-bold tracking-tight"
              style={{ fontSize: 'clamp(36px, 5vw, 60px)', fontFamily: "'Syne', sans-serif" }}
            >
              The Technology
            </h2>
            <p className="text-[#555] max-w-sm text-sm leading-relaxed">
              Three layers working together to make robot intelligence verifiable, private, and monetizable.
            </p>
          </div>
        </motion.div>

        {/* Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {cards.map((card) => {
            const Icon = card.icon;
            return (
              <motion.div
                key={card.title}
                variants={cardVariants}
                whileHover={{ scale: 1.03, y: -6 }}
                transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                className="glass-card card-3d spotlight p-8 flex flex-col gap-6 relative group overflow-hidden rounded-xl"
                style={card.featured ? {
                  borderColor: 'rgba(0, 240, 255, 0.2)',
                  background: 'rgba(0, 240, 255, 0.02)',
                } : {}}
                data-testid={`tech-card-${card.title.toLowerCase().replace(' ', '-')}`}
              >
                {card.featured && (
                  <div
                    className="absolute -top-px left-8 right-8 h-px"
                    style={{ background: 'linear-gradient(90deg, transparent, #00F0FF, transparent)' }}
                  />
                )}

                <div className="flex items-start justify-between">
                  <div
                    className="w-11 h-11 flex items-center justify-center"
                    style={{ border: '1px solid rgba(0, 240, 255, 0.2)', background: 'rgba(0, 240, 255, 0.04)' }}
                  >
                    <Icon size={18} color="#00F0FF" />
                  </div>
                  <span className="font-mono text-[10px] text-[#444] tracking-[0.2em] uppercase self-start pt-1">
                    {card.tag}
                  </span>
                </div>

                <div>
                  <h3
                    className="font-display text-white font-semibold text-xl mb-3"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    {card.title}
                  </h3>
                  <p className="text-[#666] text-sm leading-relaxed">{card.desc}</p>
                </div>

                <ul className="space-y-2 mt-auto">
                  {card.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-3 text-xs text-[#555]">
                      <span className="w-1 h-1 rounded-full bg-[#00F0FF] flex-shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>

                {/* Hover glow */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{ background: 'radial-gradient(ellipse at 50% 100%, rgba(0, 240, 255, 0.05) 0%, transparent 70%)' }}
                />
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
